import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import translations from "./Translations";

const RTL_LANG_MODES = ["ar", "ur"];

const ColumnsCustomizeModal = ({
  open = false,
  onClose,
  tableHeaders = [],
  visibleColumnKeys = [],
  onApply,
  langMode = "en",
  anchorRef,
  // Minimum number of columns that must stay checked. When >0, a checked box
  // is disabled once the draft selection hits this floor (mirrors the caller's
  // own "at least N columns visible" rule). Defaults to 0 (no floor), matching
  // this modal's original behavior for callers that don't pass it.
  minSelected = 0,
  // "Reset to default" target; falls back to "all columns" (defaultKeys) when
  // omitted, matching the original behavior where Reset to default and Select
  // all were the same action.
  defaultSelectedKeys,
  emptyStateMessage,
}) => {
  const [columnSearch, setColumnSearch] = useState("");
  const [draftColumnKeys, setDraftColumnKeys] = useState([]);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);
  const isRtl = RTL_LANG_MODES.includes(langMode);
  const t = translations?.[langMode] || translations.en;

  const allColumns = useMemo(
    () =>
      (tableHeaders || []).map((header) => ({
        value: header.value,
        label: header.label,
      })),
    [tableHeaders]
  );

  const defaultKeys = useMemo(
    () => allColumns.map((column) => column.value),
    [allColumns]
  );

  const normalizedInitialKeys = useMemo(() => {
    const availableKeys = new Set(defaultKeys);
    const selected = (visibleColumnKeys || []).filter((key) => availableKeys.has(key));
    return selected.length ? selected : defaultKeys;
  }, [defaultKeys, visibleColumnKeys]);

  const resetKeys = useMemo(() => {
    const availableKeys = new Set(defaultKeys);
    const normalized = (defaultSelectedKeys || []).filter((key) => availableKeys.has(key));
    return normalized.length ? normalized : defaultKeys;
  }, [defaultKeys, defaultSelectedKeys]);

  const minFallbackKeys = useMemo(
    () => defaultKeys.slice(0, Math.max(minSelected, 1)),
    [defaultKeys, minSelected]
  );

  useEffect(() => {
    if (!open) return;
    setDraftColumnKeys(normalizedInitialKeys);
    setColumnSearch("");
  }, [open, normalizedInitialKeys]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (
        !popoverRef.current?.contains(event.target) &&
        !anchorRef?.current?.contains(event.target)
      ) onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const viewportPadding = 8;
      const width = Math.min(360, window.innerWidth - viewportPadding * 2);
      const preferredLeft = isRtl ? rect.left : rect.right - width;
      const left = Math.min(
        Math.max(viewportPadding, preferredLeft),
        window.innerWidth - width - viewportPadding
      );
      const availableBelow = window.innerHeight - rect.bottom - viewportPadding - 8;
      const availableAbove = rect.top - viewportPadding - 8;
      const openAbove = availableBelow < 240 && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const height = Math.max(180, Math.min(430, availableHeight));
      const top = openAbove ? rect.top - height - 8 : rect.bottom + 8;

      setPopoverStyle({ top, left, width, height });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef, isRtl]);

  const filteredColumns = useMemo(() => {
    const query = columnSearch.trim().toLowerCase();
    if (!query) return allColumns;
    return allColumns.filter((column) =>
      String(column.label || "").toLowerCase().includes(query)
    );
  }, [allColumns, columnSearch]);

  const toggleColumn = (columnKey) => {
    setDraftColumnKeys((current) =>
      current.includes(columnKey)
        ? current.filter((key) => key !== columnKey)
        : [...current, columnKey]
    );
  };

  const handleApply = () => {
    // An empty selection cannot be represented by the table because it
    // intentionally falls back to its defaults. Keep at least minSelected
    // (or one) column visible.
    const nextKeys = draftColumnKeys.length ? draftColumnKeys : minFallbackKeys;
    onApply?.(nextKeys);
    onClose?.();
  };

  if (!open || !popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      dir={isRtl ? "rtl" : "ltr"}
      style={popoverStyle}
      className="fixed z-[80] flex max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_16px_35px_rgba(15,23,42,0.18)]"
    >
      <div className="flex items-center justify-between px-3 py-3">
        <h3 className="text-sm font-semibold text-slate-800">
          {t?.columnsManageTitle || "Manage columns"}
        </h3>
        <span className="text-xs text-slate-400">
          {draftColumnKeys.length} {t?.selected || "selected"}
        </span>
      </div>

      <div className="px-3 pb-2">
        <div className="relative">
          <Search
            size={14}
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${
              isRtl ? "right-3" : "left-3"
            }`}
          />
          <input
            autoFocus
            type="text"
            value={columnSearch}
            onChange={(event) => setColumnSearch(event.target.value)}
            placeholder={t?.columnsModalSearchPlaceholder || "Search fields..."}
            className={`h-9 w-full rounded-md border border-slate-200 bg-white text-sm text-slate-700 outline-none transition focus:border-[#4c6ef5] focus:ring-2 focus:ring-[#4c6ef5]/15 ${
              isRtl ? "pr-9 pl-3" : "pl-9 pr-3"
            }`}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 px-3 pb-2 text-xs">
        <button
          type="button"
          onClick={() => setDraftColumnKeys(defaultKeys)}
          className="font-medium text-[#3157d5] hover:underline whitespace-nowrap"
        >
          {t?.columnsModalSelectAll || "Select all"}
        </button>
        <span className="text-slate-300 shrink-0">•</span>
        <button
          type="button"
          onClick={() => setDraftColumnKeys(resetKeys)}
          className="font-medium text-slate-500 hover:text-slate-700 hover:underline whitespace-nowrap"
        >
          {t?.columnsResetDefault || t?.columnsModalResetDefault || "Reset to default"}
        </button>
        <span className="text-slate-300 shrink-0">•</span>
        <button
          type="button"
          onClick={() => setDraftColumnKeys(minSelected > 0 ? minFallbackKeys : [])}
          className="font-medium text-slate-500 hover:text-slate-700 hover:underline whitespace-nowrap"
        >
          {t?.columnsModalClearAll || t?.clear || "Clear"}
        </button>
      </div>

      <div className="task-list-scrollbar min-h-0 flex-1 overflow-y-auto py-1">
        {filteredColumns.length ? (
          filteredColumns.map((column) => {
            const checked = draftColumnKeys.includes(column.value);
            const isLocked = minSelected > 0 && checked && draftColumnKeys.length <= minSelected;
            return (
              <label
                key={column.value}
                className={`flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 ${
                  isLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-50"
                }`}
                title={isLocked ? (t?.minVisibleColumnsTooltip || `At least ${minSelected} columns must stay visible`) : undefined}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isLocked}
                  onChange={() => !isLocked && toggleColumn(column.value)}
                  className="h-3.5 w-3.5 rounded border-slate-300 accent-[#4c6ef5] disabled:cursor-not-allowed"
                />
                <span className="min-w-0 truncate">{column.label}</span>
              </label>
            );
          })
        ) : (
          <p className="px-3 py-8 text-center text-sm text-slate-400">
            {emptyStateMessage || t?.noResults || "No fields found"}
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-3 py-2">
        <button data-button-intent="danger"
          type="button"
          onClick={() => onClose?.()}
          className="h-8 rounded-md px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          {t?.cancel || "Cancel"}
        </button>
        <button data-button-intent="primary"
          type="button"
          onClick={handleApply}
          className="h-8 rounded-md bg-[#236DB4] px-3.5 text-sm font-semibold text-white hover:bg-[#1d5a96] focus:outline-none focus:ring-4"
        >
          {t?.apply || "Apply"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ColumnsCustomizeModal;
